import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { useApi } from '@/shared/api';
import { useContainerQuery } from '@/shared/hooks/useContainerQuery';
import { ThreadContextPanel } from './context-panel/ThreadContextPanel';
import { ConversationPane } from './panes/ConversationPane';
import { EmptyConversation } from './panes/EmptyConversation';
import { ThreadContextHost } from './panes/ThreadContextHost';
import { threadDisplayName } from './threadDisplayName';
import { useConversationActions } from './useConversationActions';
import { useConversationMessages } from './useConversationMessages';
import { useThreadMarkRead } from './useThreadMarkRead';

const CARD = 'bg-card border-hairline md:rounded-tile md:shadow-float overflow-hidden md:border';

/**
 * Direktlink auf eine Unterhaltung (/inbox/$id): dieselbe Unterhaltung und derselbe Kontext wie
 * im Postfach, ohne Liste. Keine Fläche der App führt hierher; wer ihn aus einer Mail öffnet,
 * kommt über „Zurück“ ins Postfach.
 */
export function ThreadDetailPage() {
  const { id } = useParams({ from: '/_admin/inbox/$id' });
  const api = useApi();
  const navigate = useNavigate();
  const wide = useContainerQuery(800);
  const [contextOpen, setContextOpen] = useState(false);
  const thread = useQuery({
    queryKey: ['inbox', 'thread', id],
    queryFn: () => api.messaging.get(id),
  });
  const messages = useConversationMessages(id);
  const actions = useConversationActions(id);
  const markRead = useThreadMarkRead(id, thread.data);
  const back = () => void navigate({ to: '/inbox' });

  return (
    <div ref={wide.ref} className="-mx-4 -mb-4 flex min-h-[520px] min-w-0 flex-1 gap-3.5 md:m-0">
      <section aria-label="Unterhaltung" className={`${CARD} flex min-w-0 flex-1 flex-col`}>
        {thread.data ? (
          <ConversationPane
            thread={thread.data}
            messages={messages.items}
            messagesStatus={messages}
            onSend={(text) => actions.reply.mutateAsync(text)}
            onClose={() => actions.close.mutate()}
            onNoReplyNeeded={() => actions.noReply.mutate()}
            onMarkRead={markRead}
            onOpenContext={wide.matches ? undefined : () => setContextOpen(true)}
            onBack={back}
          />
        ) : (
          <EmptyConversation
            loading={thread.isLoading}
            paused={thread.isPending && thread.fetchStatus === 'paused'}
            error={thread.error}
            onRetry={() => void thread.refetch()}
            onBack={back}
          />
        )}
      </section>
      {thread.data && (
        <ThreadContextHost
          column={wide.matches}
          open={contextOpen}
          onClose={() => setContextOpen(false)}
          title={`Kontext · ${threadDisplayName(thread.data.guestName, thread.data.subject) ?? 'Gast'}`}
        >
          <ThreadContextPanel key={thread.data.id} thread={thread.data} />
        </ThreadContextHost>
      )}
    </div>
  );
}
